import React, { use, useEffect, useRef, useState } from 'react';
import './player-quest-page.styles.scss';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import PlayerCheckpointPage from './player-checkpoint-page.component';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { getQueryFromFirestore } from '@/utils/getDataFromDbSimplified';
import { checkElementAccess } from '@/utils/checkElementAccess';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faCheckCircle, faFlag } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import linkifyHtml from 'linkify-html';

const PlayerQuestPage = ({
  gameData,
  gameElements,
  gameStats,
  teamMembers,
  playerData,
  playerProgress
}) => {

  const { questPath } = useParams();
  const [questData, setQuestData] = useState(null);
  const [access, setAccess] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questProgress, setQuestProgress] = useState(null);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const imgRef = useRef(null);
  let questListener;
  const approveRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (questListener) questListener();
    }
  }, [])

  useEffect(() => {
    if (!playerProgress || !questData) return;
    setQuestProgress(playerProgress[questData.id] || null);
  }, [playerProgress, questData])

  useEffect(() => {
    if (!playerData || !questPath) return;
    getQuestData();
  }, [playerData, questPath])

  function getQuestData() {
    getQueryFromFirestore({
      'path': `games/${gameData.gameId}/gameElements`,
      'queryArray': [{
        'field': 'type',
        'operator': '==',
        'value': 'quest'
      }, {
        'field': 'path',
        'operator': '==',
        'value': questPath
      }],
      'callback': callback,
      'handleListener': handleListener
    })
    function callback(quest) {
      if (!quest || quest.status !== 'active') {
        setLoading(false);
        setAlertMessage('Quest not found.');
        approveRef.current = () => {
          navigate(-1);
          approveRef.current = null;
        }
        return;
      }
      setQuestData(quest);
      setLoading(false);
    }
    function handleListener(unsub) {
      questListener = unsub;
    }
  }

  useEffect(() => {
    if (!questData || !playerData) return;
    if (!gameElements || !gameStats) {
      setAccess(true);
      return;
    }
    const accessResult = checkElementAccess({
      playerData,
      gameElements,
      elementId: questData.id,
      gameStats,
      teamMembers,
      type: 'quest'
    });
    if (!accessResult) {
      setAlertMessage('Quest not found.');
      approveRef.current = () => {
        navigate(-1);
        approveRef.current = null;
      }
      return;
    }
    if (!accessResult.access) {
      setAlertMessage(`You do not meet the requirements to access this quest. Missing: ${accessResult.missing.join(', ')}`);
      approveRef.current = () => {
        navigate(-1);
        approveRef.current = null;
      }
      return;
    }
    setAccess(true);
  }, [questData, playerData, gameElements, gameStats, teamMembers])

  function isCheckpointComplete(checkpoint) {
    return playerProgress?.[questData?.id]?.checkpoints?.[checkpoint.id]?.status === 'approved';
  }

  function isCheckpointPending(checkpoint) {
    return playerProgress?.[questData?.id]?.checkpoints?.[checkpoint.id]?.status === 'pending';
  }

  function isCheckpointAccessible(index) {
    if (index === 0) return true;
    const checkpoints = questData?.checkpoints || [];
    return isCheckpointComplete(checkpoints[index - 1]);
  }

  function handleCheckpointClick(checkpoint, index) {
    if (!isCheckpointAccessible(index)) return;
    navigate(`checkpoint/${checkpoint.path}`);
  }

  const checkpoints = questData?.checkpoints || [];

  return (
    <div className='player-quest-page'>
      {
        loading
          ? <div className='center'><ThreeDotsLoader /></div>
          : (questData && access)
            ? <Routes>
                <Route path='' element={
                  <div className='player-quest-content g-card'>
                    <BackButton cancel={() => navigate(-1)} />
                    <div className='g-space-1'></div>
                    <div className={`player-quest-header ${screenSize}`}>
                      <div className='quest-icon-div'>
                        <ImageContainer
                          src={questData.icon}
                          alt={`${questData.name} quest icon`}
                          className='quest-icon'
                          preview={true} />
                      </div>
                      <div className='quest-header-text'>
                        <h2>{questData.name}</h2>
                        {
                          questData.desc &&
                          <div
                            className='quest-desc'
                            dangerouslySetInnerHTML={{ __html: linkifyHtml(questData.desc) }}>
                          </div>
                        }
                      </div>
                    </div>
                    <hr />
                    <div className={`quest-layout ${screenSize}`}>

                      {/* LEFT: Map with checkpoint pins */}
                      <div className='quest-map-display'>
                        <h3>Quest Map</h3>
                        {
                          questData.mapImageUrl
                            ? <div className='qmap-image-wrapper' ref={imgRef}>
                                <img
                                  src={
                                    questData.mapImageUrl.includes('http')
                                      ? questData.mapImageUrl
                                      : `https://app.gamably.com${questData.mapImageUrl}`
                                  }
                                  alt='Quest Map'
                                  className='qmap-image'
                                  draggable={false}
                                />
                                {checkpoints.length > 1 && (
                                  <svg
                                    className='qmap-svg-overlay'
                                    viewBox='0 0 100 100'
                                    preserveAspectRatio='none'>
                                    <defs>
                                      <filter id='qmap-line-shadow' x='-20%' y='-20%' width='140%' height='140%'>
                                        <feDropShadow dx='0' dy='2' stdDeviation='1.5' floodColor='rgba(0,0,0,0.4)' />
                                      </filter>
                                    </defs>
                                    {checkpoints.slice(1).map((pt, i) => (
                                      <line
                                        key={i}
                                        x1={checkpoints[i].xPct * 100}
                                        y1={checkpoints[i].yPct * 100}
                                        x2={pt.xPct * 100}
                                        y2={pt.yPct * 100}
                                        className='qmap-line'
                                        filter='url(#qmap-line-shadow)'
                                      />
                                    ))}
                                  </svg>
                                )}
                                {checkpoints.map((pt, i) => {
                                  const complete = isCheckpointComplete(pt);
                                  const pending = isCheckpointPending(pt);
                                  const accessible = isCheckpointAccessible(i);
                                  return (
                                    <div
                                      key={pt.id}
                                      className={`qmap-pin ${complete
                                        ? 'qmap-pin--complete'
                                        : pending
                                          ? 'qmap-pin--pending'
                                          : accessible
                                            ? 'qmap-pin--available'
                                            : 'qmap-pin--locked'}`}
                                      style={{ left: `${pt.xPct * 100}%`, top: `${pt.yPct * 100}%` }}
                                    >
                                      <span className='qmap-pin-label'>{i + 1}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            : <p className='meta'>No map available for this quest.</p>
                        }
                      </div>

                      {/* RIGHT: Checkpoint buttons */}
                      <div className='quest-checkpoints'>
                        <h3>Checkpoints</h3>
                        <div className='checkpoint-button-list'>
                          {
                            checkpoints.length > 0
                              ? checkpoints.map((cp, i) => {
                                  const complete = isCheckpointComplete(cp);
                                  const pending = isCheckpointPending(cp);
                                  const accessible = isCheckpointAccessible(i);
                                  return (
                                    <button
                                      key={cp.id}
                                      className={`g-button checkpoint-btn ${complete
                                        ? 'complete'
                                        : pending
                                          ? 'pending'
                                          : accessible
                                            ? 'available'
                                            : 'locked'}`}
                                      disabled={!accessible && !complete}
                                      onClick={() => handleCheckpointClick(cp, i)}
                                    >
                                      <span className='checkpoint-number'>{i + 1}</span>
                                      <span className='checkpoint-name'>{cp.name}</span>
                                      <span className='checkpoint-status-icon'>
                                        {complete
                                          ? <FontAwesomeIcon icon={faCheckCircle} />
                                          : !accessible
                                            ? <FontAwesomeIcon icon={faLock} />
                                            : <FontAwesomeIcon icon={faFlag} />
                                        }
                                      </span>
                                    </button>
                                  );
                                })
                              : <p className='meta'>No checkpoints found for this quest.</p>
                          }
                        </div>
                      </div>

                    </div>
                  </div>
                } />
                <Route path='checkpoint/:checkpointPath' element={
                  <PlayerCheckpointPage
                    questData={questData}
                    gameData={gameData}
                    gameElements={gameElements}
                    gameStats={gameStats}
                    teamMembers={teamMembers}
                    playerData={playerData}
                    questProgress={questProgress}
                  />
                } />
              </Routes>
            : (!loading && !questData)
              ? <div className='g-card'>
                  <BackButton cancel={() => navigate(-1)} />
                </div>
              : null
      }
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={approveRef.current} />
      <ModalConfirm
        show={!!confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
    </div>
  )
}

export default PlayerQuestPage
